import React, { useState, useRef, useEffect } from 'react';
import { FiX, FiCamera, FiClipboard, FiRefreshCcw, FiTag } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import * as tf from '@tensorflow/tfjs';
import * as mobilenet from '@tensorflow-models/mobilenet';
import api from '../../services/api';
import { useUI } from '../../context/UIContext';
import { useTheme } from '../../context/ThemeContext';

const colorPalette = [
  { name: 'black', r: 0, g: 0, b: 0 },
  { name: 'white', r: 255, g: 255, b: 255 },
  { name: 'red', r: 255, g: 0, b: 0 },
  { name: 'green', r: 0, g: 255, b: 0 },
  { name: 'blue', r: 0, g: 0, b: 255 },
  { name: 'yellow', r: 255, g: 255, b: 0 },
  { name: 'pink', r: 255, g: 192, b: 203 },
  { name: 'brown', r: 165, g: 42, b: 42 },
  { name: 'gray', r: 128, g: 128, b: 128 },
  { name: 'silver', r: 192, g: 192, b: 192 },
  { name: 'gold', r: 255, g: 215, b: 0 }
];

const getColorDistance = (c1, c2) => {
  return Math.sqrt(
    Math.pow(c1.r - c2.r, 2) +
    Math.pow(c1.g - c2.g, 2) +
    Math.pow(c1.b - c2.b, 2)
  );
};

const extractImageFeatures = (imageUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 50;
      canvas.height = 50;
      ctx.drawImage(img, 0, 0, 50, 50);
      
      const imageData = ctx.getImageData(0, 0, 50, 50).data;
      let r = 0, g = 0, b = 0;
      let validPixels = 0;

      for (let i = 0; i < imageData.length; i += 4) {
        if (imageData[i+3] > 0) {
          r += imageData[i];
          g += imageData[i+1];
          b += imageData[i+2];
          validPixels++;
        }
      }

      if (validPixels === 0) resolve(null);

      r = Math.floor(r / validPixels);
      g = Math.floor(g / validPixels);
      b = Math.floor(b / validPixels);

      let closestColor = colorPalette[0];
      let minDistance = Infinity;

      colorPalette.forEach(color => {
        const distance = getColorDistance({r, g, b}, color);
        if (distance < minDistance) {
          minDistance = distance;
          closestColor = color;
        }
      });

      resolve(closestColor.name);
    };
    img.onerror = () => resolve(null);
    img.src = imageUrl;
  });
};

const VisualSearchProductCard = ({ product, closeModal }) => {
  const productId = product._id || product.id;
  const productName = product.name || 'Product';
  const productImage = product.images?.[0] || product.image || 'https://via.placeholder.com/400';
  const productPrice = product.price || 0;
  const productOriginalPrice = product.originalPrice || product.price || 0;
  
  const leftBadge = product.leftBadge || (product.isNew ? 'NEW' : '');
  const rightBadge = product.rightBadge || (product.isBestSeller ? 'BESTSELLER' : (product.discount > 0 ? 'SALE' : ''));

  return (
    <div className="product-card relative group flex flex-col h-full bg-white border border-transparent hover:border-gray-100 rounded-lg p-2 transition-all">
      {leftBadge && <span className="badge-left z-10">{leftBadge}</span>}
      {rightBadge && <span className="badge-right z-10">{rightBadge}</span>}

      <div className="product-image-wrapper relative overflow-hidden rounded-md aspect-[3/4] bg-gray-50">
        <Link to={`/product/${productId}`} onClick={closeModal} className="block w-full h-full">
          <img src={productImage} alt={productName} className="product-image w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </Link>
        <Link
          to={`/product/${productId}`}
          onClick={closeModal}
          className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-[#d60167] text-white px-4 py-2 text-xs font-semibold tracking-wider opacity-0 group-hover:opacity-100 transition-all duration-300 w-[85%] text-center rounded-sm hover:bg-[#b0005a] hover:-translate-y-1 shadow-lg z-20"
        >
          VIEW PRODUCT
        </Link>
      </div>

      <div className="product-title-container text-center mt-3 px-1">
        <Link to={`/product/${productId}`} onClick={closeModal}>
          <h3 className="product-title text-sm font-medium text-gray-800 hover:text-[#d60167] transition-colors line-clamp-2 leading-tight">
            {productName}
          </h3>
        </Link>
      </div>

      <div className="product-pricing flex justify-center items-center gap-2 mt-2 pb-2">
        {productOriginalPrice > productPrice && (
          <span className="old-price text-gray-400 line-through text-sm">Rs. {productOriginalPrice.toLocaleString()}</span>
        )}
        <span className="new-price text-[#1c1d1d] font-semibold">Rs. {productPrice.toLocaleString()}</span>
      </div>
    </div>
  );
};

const VisualSearch = () => {
  const { isCartOpen } = useUI();
  const { theme } = useTheme();
  const [settings, setSettings] = useState({
    visualSearchEnabled: true,
    visualSearchColor: '#d60167'
  });

  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [detectedTags, setDetectedTags] = useState([]);
  const [model, setModel] = useState(null);
  const [currentFileMeta, setCurrentFileMeta] = useState({ name: '', size: 0 });
  const [pastedUrlInput, setPastedUrlInput] = useState('');
  const [activeTab, setActiveTab] = useState('upload');
  
  const fileInputRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/settings/announcement');
        setSettings({
          visualSearchEnabled: res.visualSearchEnabled !== undefined ? res.visualSearchEnabled : true,
          visualSearchColor: res.visualSearchColor || '#d60167'
        });
      } catch (err) {
        console.error('Failed to fetch settings');
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const loadAIModel = async () => {
      try {
        await tf.ready();
        const loadedModel = await mobilenet.load({ version: 1, alpha: 1.0 });
        setModel(loadedModel);
      } catch (err) {
        console.error("TensorFlow initialization failed:", err);
      }
    };
    loadAIModel();
  }, []);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const resetSearch = () => {
    setSelectedImage(null);
    setSearchResults([]);
    setDetectedTags([]);
    setIsProcessing(false);
    setCurrentFileMeta({ name: '', size: 0 });
    setPastedUrlInput('');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      initiateSearch(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      initiateSearch(files[0]);
    }
  };

  const initiateSearch = (file) => {
    const imageUrl = URL.createObjectURL(file);
    setCurrentFileMeta({ name: file.name, size: file.size });
    setSelectedImage(imageUrl);
    setIsProcessing(true);
    setDetectedTags([]);
  };

  const handlePasteUrl = () => {
    if (!pastedUrlInput.trim()) {
      toast.error('Please enter a valid image URL');
      return;
    }
    try {
      new URL(pastedUrlInput);
    } catch (_) {
      toast.error('Invalid URL format');
      return;
    }
    setCurrentFileMeta({ name: 'URL Image', size: 0 });
    setSelectedImage(pastedUrlInput);
    setIsProcessing(true);
    setDetectedTags([]);
    setPastedUrlInput('');
  };

  const runSmartVisualEngine = async () => {
    if (!imageRef.current) {
      setIsProcessing(false);
      return;
    }

    try {
      const res = await api.get('/products');
      const allProducts = Array.isArray(res) ? res : (res.products || []);

      if (allProducts.length === 0) {
        setIsProcessing(false);
        return;
      }

      let aiClassTokens = [];
      let detectedLabelText = "";

      if (model) {
        const predictions = await model.classify(imageRef.current);
        if (predictions && predictions.length > 0) {
          detectedLabelText = predictions[0].className.split(',')[0].toLowerCase();
          aiClassTokens = predictions.flatMap(p => p.className.toLowerCase().split(/[\s,]+/));
        }
      }

      const lowerFileName = currentFileMeta.name.toLowerCase();
      const fileTokens = lowerFileName.split(/[\s,._-]+/);
      const allTokens = [...new Set([...aiClassTokens, ...fileTokens])].filter(t => t.length > 2);

      const mappingDict = {
        shoes: ["shoe", "sneaker", "sandal", "boot", "clog", "slipper", "footwear", "heel", "pump", "loafer", "brogue"],
        bags: ["bag", "backpack", "purse", "handbag", "wallet", "satchel", "clutch"],
        watches: ["watch", "timepiece", "chronometer", "stopwatch", "wristwatch"],
        apparel: ["apparel", "shirt", "jersey", "clothes", "gown", "dress", "suit", "coat", "jacket", "cardigan", "sweater", "jean", "pant", "garment", "tunic", "clothing", "top"],
        fragrances: ["fragrance", "perfume", "bottle", "cologne", "scent"]
      };

      let finalTargetCategory = "";
      for (const [dbCategory, keywords] of Object.entries(mappingDict)) {
        const matched = allTokens.some(token => keywords.some(kw => token.includes(kw) || kw.includes(token)));
        if (matched) {
          finalTargetCategory = dbCategory;
          break;
        }
      }

      if (!finalTargetCategory && detectedLabelText) {
        finalTargetCategory = detectedLabelText;
      }

      let filtered = allProducts.filter(p => {
        const pCatName = (p.category?.name || p.category || '').toLowerCase();
        const pNameStr = (p.name || '').toLowerCase();
        const pDescStr = (p.description || '').toLowerCase();
        const pTags = Array.isArray(p.tags) ? p.tags.map(t => t.toLowerCase()) : [];

        if (finalTargetCategory) {
          let baseWord = finalTargetCategory;
          if (baseWord.endsWith('s') && baseWord.length > 4) baseWord = baseWord.slice(0, -1);
          
          if (pCatName.includes(baseWord) || pNameStr.includes(baseWord)) {
            return true;
          }
        }

        return allTokens.some(token => 
          pNameStr.includes(token) || 
          pCatName.includes(token) || 
          pDescStr.includes(token) ||
          pTags.some(t => t.includes(token))
        );
      });

      if (filtered.length === 0) {
        filtered = allProducts.filter(p => {
          const pCatName = (p.category?.name || p.category || '').toLowerCase();
          const firstProductCat = (allProducts[0].category?.name || allProducts[0].category || '').toLowerCase();
          return pCatName.includes(firstProductCat) || firstProductCat.includes(pCatName);
        });
      }

      const tagsToDisplay = [finalTargetCategory ? finalTargetCategory.toUpperCase() : "PRODUCT"];

      const extractedColor = await extractImageFeatures(selectedImage);
      if (extractedColor) {
        tagsToDisplay.push(extractedColor.toUpperCase());
        
        filtered.sort((a, b) => {
          const aColor = a.colors?.some(c => c.toLowerCase().includes(extractedColor)) || a.name.toLowerCase().includes(extractedColor);
          const bColor = b.colors?.some(c => c.toLowerCase().includes(extractedColor)) || b.name.toLowerCase().includes(extractedColor);
          return (aColor === bColor) ? 0 : aColor ? -1 : 1;
        });
      }

      setDetectedTags(tagsToDisplay);
      setSearchResults(filtered.slice(0, 12));
      setIsProcessing(false);

    } catch (err) {
      console.error("AI engine compilation trace error:", err);
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (selectedImage && isProcessing) {
      const imgNode = imageRef.current;
      if (imgNode) {
        if (imgNode.complete) {
          runSmartVisualEngine();
        } else {
          imgNode.onload = () => runSmartVisualEngine();
        }
      }
    }
  }, [selectedImage, isProcessing]);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        resetSearch();
      }, 300);
    }
  }, [isOpen]);

  // Don't render if cart is open or visual search is disabled
  if (isCartOpen || !settings.visualSearchEnabled) return null;

  return (
    <>
      <div 
        onClick={openModal}
        className="fixed right-0 top-[50px] md:top-[60px] text-white pl-2 pr-1.5 py-2 rounded-l-[10px] shadow-md cursor-pointer transition-colors duration-300 z-[9999] flex items-center justify-center group hover:opacity-80"
        style={{ backgroundColor: settings.visualSearchColor }}
        title="Smart Visual Search"
      >
        <FiCamera size={15} strokeWidth={2.5} className="group-hover:scale-110 transition-transform" />
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div 
            className="bg-white rounded-lg shadow-2xl w-full max-w-6xl relative flex flex-col pt-12 pb-10 px-6 sm:px-10 animate-fade-in max-h-[95vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={closeModal}
              className="absolute top-4 right-4 sm:top-6 sm:left-6 text-gray-500 hover:text-[#d60167] transition-colors p-2 z-10 bg-white rounded-full shadow-sm border border-gray-100"
            >
              <FiX size={24} strokeWidth={2} />
            </button>

            <div className="overflow-y-auto hide-scrollbar w-full h-full pr-2">
              {!selectedImage ? (
                <div className="flex flex-col items-center justify-center h-full min-h-[60vh] mt-4">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl sm:text-[28px] font-bold text-black mb-3">Smart Visual Search</h2>
                    <p className="text-[15px] text-gray-800">Upload any image or paste a URL to instantly find visually similar products via Local Embedded AI</p>
                  </div>

                  <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
                    <button
                      onClick={() => setActiveTab('upload')}
                      className={`flex items-center gap-2 px-6 py-2 rounded-md transition-colors font-medium ${
                        activeTab === 'upload'
                          ? 'bg-white shadow-sm text-[#d60167]'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <FiCamera size={16} />
                      Upload Image
                    </button>
                    <button
                      onClick={() => setActiveTab('url')}
                      className={`flex items-center gap-2 px-6 py-2 rounded-md transition-colors font-medium ${
                        activeTab === 'url'
                          ? 'bg-white shadow-sm text-[#d60167]'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      <FiClipboard size={16} />
                      Paste URL
                    </button>
                  </div>

                  {activeTab === 'upload' && (
                    <div 
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current.click()}
                      className={`w-full max-w-2xl border-2 border-dashed rounded-lg py-16 px-4 flex flex-col items-center justify-center cursor-pointer transition-colors ${
                        isDragging ? 'border-[#d60167] bg-pink-50' : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileSelect} 
                        accept="image/*" 
                        className="hidden" 
                      />
                      <p className="text-[16px] text-gray-500 mb-6 font-medium text-center">Drag and drop your image here or click to browse</p>
                      
                      <div className="relative w-10 h-10 text-[#d60167]">
                        <div className="absolute top-0 right-0 w-7 h-7 border-[2.5px] border-dotted border-[#d60167] rounded-sm"></div>
                        <div className="absolute bottom-0 left-0 w-7 h-7 border-[2.5px] border-[#d60167] rounded-sm bg-white shadow-sm"></div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'url' && (
                    <div className="w-full max-w-2xl border-2 border-gray-300 rounded-lg p-6 flex flex-col items-center gap-4 bg-white">
                      <p className="text-[16px] text-gray-500 font-medium">Paste an image URL below</p>
                      <div className="w-full flex flex-col sm:flex-row gap-3">
                        <input
                          type="url"
                          placeholder="https://example.com/image.jpg"
                          value={pastedUrlInput}
                          onChange={(e) => setPastedUrlInput(e.target.value)}
                          className="flex-1 w-full border border-gray-300 rounded px-4 py-3 text-sm focus:outline-none focus:border-[#d60167]"
                        />
                        <button
                          onClick={handlePasteUrl}
                          disabled={isProcessing || !pastedUrlInput.trim()}
                          className="flex items-center justify-center gap-2 px-6 py-3 bg-[#d60167] text-white rounded hover:bg-[#b0005a] disabled:opacity-50 transition-colors whitespace-nowrap font-medium min-w-[140px]"
                          style={{ backgroundColor: settings.visualSearchColor }}
                        >
                          <FiClipboard size={16} strokeWidth={2.5} />
                          Search with URL
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Enter the direct link to an image (JPG, PNG, WEBP)</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col h-full w-full">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pt-2 border-b pb-4 gap-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {isProcessing ? 'AI Neural Inference Active...' : 'Visually Similar Products'}
                      </h2>
                      {!isProcessing && detectedTags.length > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs font-medium text-gray-500 flex items-center gap-1"><FiTag size={12}/> AI Classification:</span>
                          {detectedTags.map((tag, idx) => (
                            <span key={idx} className="bg-pink-100 text-[#d60167] px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider border border-pink-200" style={{ backgroundColor: settings.visualSearchColor + '20', color: settings.visualSearchColor }}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {!isProcessing && (
                      <button onClick={resetSearch} className="flex items-center text-sm font-medium hover:underline bg-pink-50 px-4 py-2 rounded-md border border-pink-100" style={{ color: settings.visualSearchColor, borderColor: settings.visualSearchColor + '40' }}>
                        <FiRefreshCcw className="mr-2" /> Search Another Image
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col lg:flex-row gap-8">
                    <div className="w-full lg:w-[280px] flex-shrink-0">
                      <div className="sticky top-0">
                        <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">Target Image</p>
                        <div className="relative rounded-lg overflow-hidden border border-gray-200 shadow-sm aspect-[3/4] bg-gray-100">
                          <img 
                            ref={imageRef}
                            src={selectedImage} 
                            alt="Uploaded target object" 
                            className="w-full h-full object-cover" 
                            crossOrigin="anonymous"
                          />
                          {isProcessing && (
                            <>
                              <div className="absolute inset-0 bg-[#d60167]/10 z-10 mix-blend-overlay" style={{ backgroundColor: settings.visualSearchColor + '20' }}></div>
                              <div className="absolute left-0 right-0 h-1 shadow-[0_0_20px_rgba(214,1,103,1)] z-20 animate-scan" style={{ backgroundColor: settings.visualSearchColor }}></div>
                              <div className="absolute inset-0 flex flex-col items-center justify-center z-30">
                                <div className="bg-white/95 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg flex items-center gap-2" style={{ color: settings.visualSearchColor }}>
                                  <div className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: settings.visualSearchColor }}></div>
                                  Processing...
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">
                        {isProcessing ? 'Calculating Vectors...' : `Found ${searchResults.length} Relevant Matches`}
                      </p>
                      
                      {isProcessing ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(skeleton => (
                            <div key={skeleton} className="animate-pulse flex flex-col gap-2 bg-white p-2 rounded-lg border border-gray-100">
                              <div className="bg-gray-200 aspect-[3/4] rounded-md w-full"></div>
                              <div className="h-4 bg-gray-200 rounded w-3/4 mt-2"></div>
                              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                            </div>
                          ))}
                        </div>
                      ) : searchResults.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 gap-y-6">
                          {searchResults.map((product, idx) => (
                            <VisualSearchProductCard key={`${product._id}-${idx}`} product={product} closeModal={closeModal} />
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-20 text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                          <p className="text-lg font-medium text-gray-700">No exact matches found.</p>
                          <button onClick={resetSearch} className="mt-6 px-6 py-2 text-white rounded font-medium hover:opacity-80 transition-colors" style={{ backgroundColor: settings.visualSearchColor }}>
                            Upload Different Image
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes scan {
          0% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .animate-scan {
          animation: scan 2s ease-in-out infinite;
        }
      `}} />
    </>
  );
};

export default VisualSearch;